import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "data/hooks/useQuery";

import Card from "features/shared/components/card/Card";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { getQueryParams } from "features/shared/helpers/queryParams";
import IAppState from "infrastructure/store/IAppState";
import CustomerState from "infrastructure/store/customer/CustomerState";
import { Deposit_Get } from "data/endpoints";
import DepositInfoCard from "../components/DepositInfoCard";
import DepositUsageDetailHeader from "../components/DepositUsageDetailHeader";
import DepositUsageDetail from "../components/DepositUsageDetail";
import PaymentUsageDTO from "features/payments/dtos/PaymentUsageDTO";
import { DepositSimplifiedDTO } from "../dtos/DepositSimplifiedDTO";

interface Props {
  depositId: string;
}

const DepositDetailSection = ({ depositId }: Props) => {
  const customer = useSelector<IAppState, CustomerState>((s) => s.customer).customer;

  const { data, loadStatus } = useQuery<DepositSimplifiedDTO>(
    {
      url: Deposit_Get,
      routeParams: getRouteParams("depositId", depositId),
      params: customer.customerId ? getQueryParams("CustomerId", customer.customerId) : undefined,
    },
    {},
    [depositId, customer.customerId]
  );

  const depositItem = useMemo(() => data, [data]);

  const usages: PaymentUsageDTO[] = useMemo(
    () => depositItem?.payment?.paymentUsage ?? [],
    [depositItem?.payment?.paymentUsage]
  );

  const visibleUsages = useMemo(
    () => usages.filter(u => !!u.invoiceUsage),
    [usages]
  );

  return (
    <NoContentWrapper data={depositItem} loadStatus={loadStatus} noContentAsAlert>
      {depositItem && <DepositInfoCard deposit={depositItem} />}

      <div className="mt-sm-4">
        {visibleUsages.length > 0 && <DepositUsageDetailHeader />}

        {visibleUsages.map((u, i) => (
          <Card className="py-2 px-2 px-sm-3 mb-2" key={i}>
            <DepositUsageDetail paymentUsage={u} />
          </Card>
        ))}
      </div>
    </NoContentWrapper>
  );
};

export default DepositDetailSection;
